<template>
  <section class="info-section">
    <div class="info-card">
      <header class="info-header">
        <h3>{{ title }}</h3>
      </header>

      <div class="info-content">
        <slot />
      </div>

      <div v-if="showApplyToVariants" class="right-section">
        <div :class="['apply-variants', { disabled: isSaved || isSaving }]">
          <input
            type="checkbox"
            :id="checkboxId"
            :checked="applyToVariants"
            @change="$emit('update:applyToVariants', $event.target.checked)"
            :disabled="isSaved || isSaving || isProductDisabled"
          />
          <label :for="checkboxId">{{ applyVariantsText }}</label>
        </div>

        <div class="actions-row">
          <Button
            primary
            md
            @click="$emit('saveChanges')"
            :buttonStyle="{ width: saveButtonWidth }"
            :disabled="isSaved || isSaving || isProductDisabled"
          >
            <span class="save-btn-content">
              <span v-if="isSaving" class="spinner"></span>
              <Icons
                v-else-if="showSuccessState"
                name="Checked"
                color="#B4B4B4"
                size="md"
              />
              {{ currentButtonText ? currentButtonText : saveButtonText }}
            </span>
          </Button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { Button, Icons } from '@n11/so-ui-components';

const store = useStore();
const isProductDisabled = computed(() => store.getters['products/isProductDisabled']);

const props = defineProps({
  title: String,
  showApplyToVariants: {
    type: Boolean,
    default: false
  },
  applyToVariants: {
    type: Boolean,
    default: true
  },
  applyVariantsText: {
    type: String,
    default: 'Değişiklikleri tüm varyantlara uygula.'
  },
  saveButtonText: {
    type: String,
    default: 'Değişiklikleri Kaydet'
  },
  savedButtonText: {
    type: String,
    default: 'Değişiklikler Kaydedildi'
  },
  saveButtonWidth: {
    type: String,
    default: '212px'
  },
  isSaving: {
    type: Boolean,
    default: false
  },
  isSaved: {
    type: Boolean,
    default: false
  },
  showSuccessState: {
    type: Boolean,
    default: false
  },

  checkboxId: {
    type: String,
    required: true
  }
});

defineEmits(['update:applyToVariants', 'saveChanges']);

const currentButtonText = computed(() => {
  return props.showSuccessState ? props.savedButtonText : props.saveButtonText;
});

</script>

<style style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.info-section {
  width: 100%;
}
.info-card {
  width: 100%;
  border-bottom: 1px solid #ddd;
  flex: 1;
  padding: 24px;
  min-width: 300px;
}
.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.right-section {
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: flex-end;
}

.apply-variants {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
}

.apply-variants input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: $colorN80;
}

.apply-variants label {
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  cursor: pointer;
  user-select: none;
  color: #333;
}

.actions-row {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  align-items: center;
}

.save-btn-content {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.apply-variants.disabled {
  pointer-events: none;
  opacity: 0.5;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.right-section.disabled {
  .apply-variants {
    pointer-events: none;
    opacity: 0.6;

    input[type="checkbox"] {
      opacity: 0.6;
      cursor: not-allowed;
    }

    label {
      opacity: 0.6;
      cursor: not-allowed;
      color: $colorN60;
    }
  }

  .actions-row {
    button {
      opacity: 0.6;
      cursor: not-allowed !important;
      pointer-events: none;
    }

    :deep(button) {
      cursor: not-allowed !important;
    }
  }
}
</style>
